package com.essntl.features.task.ui.addedittask

import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.viewmodel.*
import com.essntl.features.itinerary.domain.model.ItineraryServiceModel
import com.essntl.features.itinerary.domain.repository.ItineraryRepository
import com.essntl.features.profile.domain.repository.ProfileRepository
import com.essntl.features.profile.domain.use_case.GetCurrentUserUseCase
import com.essntl.features.proposal.domain.model.ProposalModel
import com.essntl.features.task.domain.model.TaskModel
import com.essntl.features.task.domain.repository.TaskRepository
import com.essntl.features.task.domain.use_case.AddTaskUseCase
import com.essntl.features.task.domain.use_case.UpdateTaskUseCase
import com.essntl.features.task.ui.sharedtask.SharedTaskViewModel
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class AddEditTaskViewModel(
    private val taskRepository: TaskRepository,
    private val profileRepository: ProfileRepository,
    private val addTaskUseCase: AddTaskUseCase,
    private val updateTaskUseCase: UpdateTaskUseCase,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val state: MutableStateFlow<AddEditTaskState>,
    itineraryRepository: ItineraryRepository,
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
) : ViewModel {

    private val sharedTaskViewModel =
        SharedTaskViewModel(
            itineraryRepository = itineraryRepository,
            scope = scope,
            loadingManager = loadingManager,
        )

    init {
        collectSharedTaskState()
        getNextProfilePage()
        getCurrentProfile()

        // Get Current User Profile Use Case
        collectSuccessEvent(
            flow = getCurrentUserUseCase.successEvent,
            getState = { state.value.getCurrentProfile },
            setState = { newState -> state.update { it.copy(getCurrentProfile = newState) } },
        )
    }

    private fun getCurrentProfile() =
        scope.launch {
            getCurrentUserUseCase.get()
        }


    private var getSharedTaskStateJob: Job? = null
    private fun collectSharedTaskState() {
        // collecting sharedTaskState
        getSharedTaskStateJob?.cancel()
        getSharedTaskStateJob = scope.launch {
            sharedTaskViewModel.state.collect { data ->
                state.update {
                    it.copy(
                        sharedTaskState = data,
                    )
                }
            }
        }
    }

    fun onEvent(event: AddEditTaskEvent) {
        when (event) {
            is AddEditTaskEvent.GetTask -> getTask(event)
            is AddEditTaskEvent.GetItinerary -> getItinerary(event)
            is AddEditTaskEvent.GetItineraryService -> getItineraryService(event)
            is AddEditTaskEvent.GetProposal -> getProposal(event)
            is AddEditTaskEvent.UpdateTaskFields -> updateTaskFields(event)
            is AddEditTaskEvent.AddTask -> addTask()
            is AddEditTaskEvent.EditTask -> editTask()
            is AddEditTaskEvent.ClearTaskState -> clearTaskState()
            is AddEditTaskEvent.ClearSuccessState -> clearSuccessState()
            is AddEditTaskEvent.LoadMoreProfiles -> getNextProfilePage()
        }
    }

    private fun getNextProfilePage() =
        getPaginationData(
            getData = {
                val nextPage = state.value.getAllProfile.loadedPages + 1L
                profileRepository.getAll(page = nextPage)
            },
            getState = { state.value.getAllProfile },
            setState = { newState ->
                state.update {
                    it.copy(
                        getAllProfile = newState,
                    )
                }
            },
        )

    private var getTaskJob: Job? = null
    private fun getTask(event: AddEditTaskEvent.GetTask) {
        if (state.value.getTask.data?.id == event.taskId) return

        state.value.task.value = TaskModel.empty.copy(id = event.taskId)

        // Make sure to cancel the previous job
        // The user can switch between ta quickly before the job is finished,
        // In that case we don't want to show the result of the previous job
        getTaskJob?.cancel()
        getTaskJob = getData(
            getData = { taskRepository.getById(event.taskId) },
            getState = { state.value.getTask },
            setState = { newState ->
                state.update {
                    it.copy(
                        getTask = newState,
                        isEditEnabled = false,
                    )
                }
            },
            onSuccess = { task ->
                val getItinerary =
                    sharedTaskViewModel.state.value.getAllItineraries.data
                        .orEmpty()
                        .none {
                            task.itineraryService?.itineraryId == it.id ||
                                task.itinerary?.id == it.id
                        }


                if (getItinerary) {
                    if (!task.itineraryService?.itineraryId.isNullOrEmpty()) {
                        sharedTaskViewModel.getAllItineraries(listOf(task.itineraryService?.itineraryId ?: "").toSet())
                    } else if (!task.itinerary?.id.isNullOrEmpty()) {
                        sharedTaskViewModel.getAllItineraries(listOf(task.itinerary?.id ?: "").toSet())
                    }
                }

                task.updatedBy?.let {
                    getProfile(it)
                }

                state.value.task.value = task
            },
        )
    }

    private fun getProfile(id: String) =
        getData(
            getData = { profileRepository.getById(id) },
            getState = { state.value.getProfile },
            setState = { newState ->
                state.update {
                    it.copy(getProfile = newState)
                }
            },
        )

    private fun getItinerary(event: AddEditTaskEvent.GetItinerary) {
        state.value.task.value = state.value.task.value.copy(itinerary = event.itineraryId)
    }

    private fun getItineraryService(event: AddEditTaskEvent.GetItineraryService) {
        state.value.task.value = state.value.task.value.copy(
            itineraryService = ItineraryServiceModel.empty.copy(id = event.itineraryServiceId),
        )
    }

    private fun getProposal(event: AddEditTaskEvent.GetProposal) {
        state.value.task.value = state.value.task.value.copy(
            proposal = ProposalModel.empty.copy(id = event.proposalId),
        )
    }

    private fun updateTaskFields(event: AddEditTaskEvent.UpdateTaskFields) {
        state.value.task.value = event.task
        val newTask = state.value.getTask.data?.getChangedFields(state.value.task.value) ?: return
        state.update {
            it.copy(isEditEnabled = !newTask.isEmpty)
        }
    }

    private fun addTask() =
        scope.launch {
            addTaskUseCase.add(
                task = state.value.task.value,
                getState = { state.value.addEditTask },
                setState = { state.value = state.value.copy(addEditTask = it) },
                onSuccess = { task ->
                    task.updatedBy?.let {
                        getProfile(it)
                    }

                    state.update {
                        it.copy(
                            getTask = State.success(task),
                        )
                    }

                    state.value.task.value = task
                },
            )
        }

    private fun editTask() =
        scope.launch {
            val newTask = state.value.getTask.data?.getChangedFields(state.value.task.value) ?: return@launch

            if (newTask.isEmpty) return@launch

            updateTaskUseCase.update(
                task = newTask.copy(
                    itineraryService = state.value.task.value.itineraryService,
                    owner = state.value.task.value.owner,
                    itinerary = state.value.task.value.itinerary,
                ),
                getState = { state.value.addEditTask },
                setState = { state.value = state.value.copy(addEditTask = it) },
                onSuccess = { task ->
                    task.updatedBy?.let {
                        getProfile(it)
                    }

                    state.update {
                        it.copy(
                            getTask = State.success(task),
                        )
                    }

                    state.value.task.value = task
                },
            )
        }

    private fun clearSuccessState() {
        state.value = state.value.copy(
            addEditTask = State(),
            isEditEnabled = false,
        )
    }

    private fun clearTaskState() {
        state.value = state.value.copy(
            getTask = State(),
            getProfile = State(),
            addEditTask = State(),
            isEditEnabled = false,
        )

        state.value.task.value = TaskModel.empty
        setTaskOwner()

        getTaskJob?.cancel()
    }

    private fun setTaskOwner() {
        if (!state.value.getCurrentProfile.isSuccess) return
        if (state.value.getTask.data?.owner != null) return

        state.value.task.value = state.value.task.value.copy(owner = state.value.getCurrentProfile.data)
    }
}